<template>
	<k-panel class="ks-videoeditor" fill-height>
		<template #title>Video editor</template>

		<template #buttons>
			<div class="ks-videoeditor-title-field">
				<k-text-field v-model="project_title" placeholder="Project title" hide-details dense></k-text-field>
			</div>
			<k-btn size="m" color="success" :disabled="end_time <= 0" @click="save">Save</k-btn>
			<k-btn size="m" color="primary" :disabled="end_time <= 0" @click="download">Export JSON</k-btn>
			<k-btn size="m" color="primary" @click="$refs.import_file.click()">Import JSON</k-btn>
			<input ref="import_file" type="file" accept=".json,application/json" style="display: none;" @change="import_file">
            <k-btn size="s" icon icon-name="movie_edit" @click="$router.push('/cloudrec/event/'+event_uuid)"></k-btn>

		</template>

		<template #default>
			<div class="ks-videoeditor-body">
				<div class="ks-videoeditor-top" :style="{gridTemplateColumns: sources_width + 'px 12px minmax(0, 1fr) 12px ' + inspector_width + 'px'}">
					<source-panel
						:event_uuid="event_uuid"
						@add-clip="add_clip"
						@add-overlay="add_overlay"
						@add-audio="add_audio"></source-panel>

					<splitter @start="split_start" @move="move_split('left', $event)"></splitter>

					<div class="ks-videoeditor-preview">
						<preview-screen
							ref="preview"
							:timeline="timeline"
							:overlays="overlays"
							:audios="audios"
							:layout="layout"
							:hidden_ids="hidden_ids"
							:muted_ids="muted_ids"
							:track_z_map="track_z_map"
							:selected_frame="selected_frame"
							:preview_low="preview_low"
							@aspect="set_aspect"
							@loaded="sync_players"
							@pick="pick_at"
							@update="update_item"></preview-screen>
						<player-controls
							ref="controls"
							:playing="playing"
							:playhead="playhead"
							:end_time="end_time"
							:preview_low.sync="preview_low"
							@rewind="seek(0)"
							@toggle="toggle_play"></player-controls>
					</div>

					<splitter @start="split_start" @move="move_split('right', $event)"></splitter>

					<inspector
						:clip="selected"
						:pair_id="pair_id"
						:pair="pair"
						:crossfades_out="selected_crossfades_out"
						:overlay="selected_overlay"
						:audio="selected_audio"
						@update="update_item"
						@preset="apply_preset"
						@set-start="set_clip_start"
						@set-in="set_in"
						@set-out="set_out"
						@set-duration="set_duration"
						@set-quality="set_quality"
						@start-at-playhead="start_at_playhead"
						@split="split"
						@duplicate-clip="duplicate_clip"
						@remove-clip="remove_clip"
						@duplicate-overlay="duplicate_overlay"
						@remove-overlay="remove_overlay"
						@duplicate-audio="duplicate_audio"
						@remove-audio="remove_audio"
						@pair-transition="set_pair_transition"></inspector>
				</div>

				<splitter direction="h" @start="split_start" @move="move_split('bottom', $event)"></splitter>

				<timeline
					ref="timeline"
					:style="timeline_height ? {height: timeline_height + 'px', maxHeight: 'none', flex: '0 0 auto'} : {}"
					:tracks="tracks"
					:timeline="timeline"
					:layout="layout"
					:overlays="overlays"
					:audios="audios"
					:end_time="end_time"
					:playhead="playhead"
					:zoom.sync="zoom"
					:selected_id="selected_id"
					:pair_id="pair_id"
					:selected_overlay_id="selected_overlay_id"
					:selected_audio_id="selected_audio_id"
					:active_track="active_track"
					:can_split="!!selected"
					:can_delete="!!selected || !!selected_overlay || !!selected_audio"
					:can_clear="timeline.length > 0 || overlays.length > 0 || audios.length > 0"
					@update="update_item"
					@split="split"
					@delete="delete_selected"
					@clear="clear_timeline"
					@add-track="add_track"
					@select-clip="select"
					@pair-clip="pair_clip"
					@select-overlay="select_overlay"
					@select-audio="select_audio"
					@clear-selection="clear_selection"
					@select-transition="select_transition"
					@pause="pause"
					@scrub="scrub"
					@trim="on_trim"
					@drag-end="sync_players"
					@activate-track="activate_track"
					@move-track="move_track"
					@rename-track="rename_track"
					@toggle-track="toggle_track"
					@toggle-track-mute="toggle_track_mute"
					@remove-track="remove_track"></timeline>
			</div>
		</template>
	</k-panel>
</template>

<script>
import {DEFAULT_IMAGE_SECONDS, FRAME_ASPECT, IMAGE_MAX_SECONDS, MIN_LEN} from './video_editor/constants.js'
import {build_edl, parse_edl} from './video_editor/edl.js'
import {place_overlay} from './video_editor/frame.js'
import {compute_layout, fit_start, max_len, pick_item, track_end} from './video_editor/layout.js'
import {count_tracks, default_active, default_counter, default_tracks, KIND_LABELS} from './video_editor/tracks.js'
import {out_transition} from './video_editor/utils.js'
import Inspector from './video_editor/Inspector.vue'
import PlayerControls from './video_editor/PlayerControls.vue'
import PreviewScreen from './video_editor/PreviewScreen.vue'
import SourcePanel from './video_editor/SourcePanel.vue'
import Splitter from './video_editor/Splitter.vue'
import Timeline from './video_editor/Timeline.vue'

// Holds the project (tracks, clips, overlays, audio), the selection and the playback clock;
// the panels in video_editor/ show it and report edits back as events.
export default {
	components: {Inspector, PlayerControls, PreviewScreen, SourcePanel, Splitter, Timeline},
	props: {
		event_uuid: String
	},
	data () {
		return {
			project_title: "",

			// Timeline
			timeline: [],
			selected_id: undefined,
			// Second clip picked with shift for a transition between two clips
			pair_id: undefined,
			overlays: [],
			// Tracks of all kinds in one list; the top row is drawn in front
			tracks: default_tracks(),
			active_track: default_active(),
			selected_overlay_id: undefined,
			audios: [],
			selected_audio_id: undefined,
			zoom: 40,

			// Layout (px); timeline_height null = sized by content
			sources_width: 280,
			inspector_width: 280,
			timeline_height: null,

			// Preview plays the lowest quality of each clip to keep playback light
			preview_low: true,

			// Preview
			playhead: 0,
			playing: false
		}
	},
	created () {
		// Non-reactive state
		this.next_id = 1;
		this.track_counter = default_counter();
		this.raf = undefined;
		this.last_ts = 0;
		this.pos = 0;
		// {clip, side} while an edge of a clip is dragged
		this.trimming = null;
		this.split_origin = undefined;
	},
	mounted () {
		window.addEventListener("keydown", this.key_down);
	},
	beforeDestroy () {
		window.removeEventListener("keydown", this.key_down);
		cancelAnimationFrame(this.raf);
	},
	watch: {
		audios: {
			handler () {
				this.$nextTick(this.sync_players);
			},
			deep: true
		},
		overlays: {
			handler () {
				this.$nextTick(this.sync_players);
			},
			deep: true
		},
		track_z_map () {
			this.$nextTick(this.sync_players);
		},
		timeline: {
			handler () {
				this.$nextTick(this.sync_players);
			},
			deep: true
		},
		layout () {
			// Wait for video elements of added/removed clips to render
			this.$nextTick(this.sync_players);
		},
		hidden_ids () {
			this.$nextTick(this.sync_players);
		},
		end_time (val) {
			if (this.playhead > val) this.playhead = val;
			if (this.pos > val) this.pos = val;
		},
		playhead () {
			if (!this.playing) this.sync_players();
		}
	},
	computed: {
		layout () {
			return compute_layout(this.timeline);
		},
		hidden_ids () {
			return _.keyBy(_.filter(this.tracks, (t) => t.hidden), "id");
		},
		muted_ids () {
			return _.keyBy(_.filter(this.tracks, (t) => t.muted), "id");
		},
		// Rows higher in the list are drawn in front; video clips and overlays share one stack
		track_z_map () {
			const n = this.tracks.length;
			return _.fromPairs(_.map(this.tracks, (t, i) => [t.id, (n - i) * 1000]));
		},
		total () {
			return this.layout.length ? _.max(_.map(this.layout, (l) => l.end)) : 0;
		},
		// Latest end across all tracks, so the playhead can move even when the video track is empty
		end_time () {
			const ends = [this.total]
				.concat(_.map(this.overlays, (o) => o.start + o.duration))
				.concat(_.map(this.audios, (a) => a.start + a.duration));
			return _.max(ends);
		},
		selected () {
			return _.find(this.timeline, {id: this.selected_id});
		},
		// The two shift-selected clips; consecutive means no other clip lies between them on the track
		pair () {
			const other = _.find(this.timeline, {id: this.pair_id});
			if (!this.selected || !other) return undefined;
			const [first, second] = _.sortBy([this.selected, other], "start");
			const consecutive = first.track == second.track && !_.some(this.timeline, (c) => c.track == first.track && c.id != first.id && c.id != second.id && c.start > first.start && c.start < second.start);
			return {first, second, consecutive};
		},
		selected_crossfades_out () {
			return !!this.selected && _.some(this.layout, (l) => l.xfade && l.xfade.prev_id == this.selected.id);
		},
		selected_overlay () {
			return _.find(this.overlays, {id: this.selected_overlay_id});
		},
		// The item whose frame can be moved and resized in the preview
		selected_frame () {
			return this.selected_overlay || this.selected;
		},
		selected_audio () {
			return _.find(this.audios, {id: this.selected_audio_id});
		}
	},
	methods: {
		// ---- Adding items (from the source panel)
		add_clip ({type, title, url, poster_url, qualities, duration, source}) {
			if (!isFinite(duration) || duration <= 0) {
				this.$message.warning("Clip has no duration");
				return;
			}
			const clip = {
				id: this.next_id++,
				type: type || "video",
				track: this.active_track.video,
				start: track_end(this.timeline, this.active_track.video),
				volume: 100,
				muted: false,
				opacity: 100,
				x: 0,
				y: 0,
				w: 100,
				aspect: FRAME_ASPECT,
				placed: false,
				title: title,
				url: url,
				poster_url: poster_url || undefined,
				qualities: qualities || [],
				source: source,
				source_duration: type == "image" ? IMAGE_MAX_SECONDS : duration,
				in: 0,
				out: duration,
				transition: {type: "cut", duration: 1},
				transition_out: {type: "cut", duration: 1}
			};
			this.timeline.push(clip);
			this.select(clip);
		},
		add_overlay ({title, url, source}) {
			const overlay = {
				id: this.next_id++,
				title: title,
				url: url,
				source: source,
				start: this.current_time(),
				duration: DEFAULT_IMAGE_SECONDS,
				track: this.active_track.overlay,
				opacity: 100,
				x: 0,
				y: 0,
				w: 100,
				aspect: FRAME_ASPECT,
				// Until the user moves it, the overlay is refitted to the frame once the image size is known
				placed: false,
				transition: {type: "cut", duration: 1},
				transition_out: {type: "cut", duration: 1}
			};
			this.overlays.push(overlay);
			this.select_overlay(overlay);
		},
		add_audio ({title, url, source, duration}) {
			const audio = {
				id: this.next_id++,
				title: title,
				url: url,
				source: source,
				start: this.current_time(),
				in: 0,
				duration: duration,
				source_duration: duration,
				volume: 100,
				track: this.active_track.audio
			};
			this.audios.push(audio);
			this.select_audio(audio);
		},

		// ---- Selection
		select (clip) {
			this.selected_id = clip.id;
			this.pair_id = undefined;
			this.active_track.video = clip.track;
			this.selected_overlay_id = undefined;
			this.selected_audio_id = undefined;
		},
		select_overlay (overlay) {
			this.selected_overlay_id = overlay.id;
			this.active_track.overlay = overlay.track;
			this.selected_id = undefined;
			this.pair_id = undefined;
			this.selected_audio_id = undefined;
		},
		select_audio (audio) {
			this.selected_audio_id = audio.id;
			this.active_track.audio = audio.track;
			this.selected_id = undefined;
			this.pair_id = undefined;
			this.selected_overlay_id = undefined;
		},
		pair_clip (clip) {
			this.pair_id = clip.id;
		},
		clear_selection () {
			this.selected_id = undefined;
			this.pair_id = undefined;
			this.selected_overlay_id = undefined;
			this.selected_audio_id = undefined;
		},
		delete_selected () {
			if (this.selected) this.remove_clip(this.selected);
			else if (this.selected_overlay) this.remove_overlay(this.selected_overlay);
			else if (this.selected_audio) this.remove_audio(this.selected_audio);
		},
		// Selects the frontmost visible overlay or clip under a point of the preview (in % of the frame)
		pick_at (point) {
			const best = pick_item({overlays: this.overlays, layout: this.layout, hidden_ids: this.hidden_ids, track_z_map: this.track_z_map}, point.x, point.y, this.current_time());
			if (!best) this.clear_selection();
			else if (best.overlay) this.select_overlay(best.overlay);
			else this.select(best.clip);
		},

		// ---- Tracks
		track_items (kind) {
			return {video: this.timeline, overlay: this.overlays, audio: this.audios}[kind];
		},
		kind_count (kind) {
			return _.filter(this.tracks, {kind: kind}).length;
		},
		reset_tracks () {
			this.tracks = default_tracks();
			this.active_track = default_active();
			this.track_counter = default_counter();
		},
		add_track (kind) {
			let id;
			do { id = "t" + this.next_id++; } while (_.some(this.tracks, {id: id}));
			this.track_counter[kind]++;
			this.tracks.unshift({id: id, kind: kind, name: KIND_LABELS[kind] + " " + this.track_counter[kind], hidden: false});
			this.active_track[kind] = id;
		},
		remove_track (id) {
			const track = _.find(this.tracks, {id: id});
			if (!track || this.kind_count(track.kind) <= 1) return;
			// Items of the removed track move to the neighbouring track of the same kind
			const same = _.filter(this.tracks, {kind: track.kind});
			const pos = same.indexOf(track);
			const target = same[pos > 0 ? pos - 1 : 1];
			_.each(this.track_items(track.kind), (item) => { if (item.track == id) item.track = target.id; });
			this.tracks = _.reject(this.tracks, {id: id});
			if (this.active_track[track.kind] == id) this.active_track[track.kind] = target.id;
		},
		activate_track (kind, id) {
			this.active_track[kind] = id;
		},
		move_track (id, target_id) {
			const from = _.findIndex(this.tracks, {id: id});
			const target = _.findIndex(this.tracks, {id: target_id});
			if (from < 0 || target < 0 || from == target) return;
			const moved = this.tracks.splice(from, 1)[0];
			this.tracks.splice(target, 0, moved);
		},
		rename_track (id, name) {
			const track = _.find(this.tracks, {id: id});
			if (track) track.name = name;
		},
		toggle_track (id) {
			const track = _.find(this.tracks, {id: id});
			if (track) track.hidden = !track.hidden;
		},
		toggle_track_mute (id) {
			const track = _.find(this.tracks, {id: id});
			if (track) this.$set(track, "muted", !track.muted);
		},

		// ---- Editing items
		update_item (item, patch) {
			_.each(patch, (value, key) => { this.$set(item, key, value); });
		},
		set_aspect (item, aspect) {
			item.aspect = aspect;
			if (!item.placed) place_overlay(item, "full");
		},
		apply_preset (item, preset) {
			place_overlay(item, preset);
			item.placed = true;
		},
		start_at_playhead (item) {
			const t = this.current_time();
			if (_.includes(this.timeline, item)) this.set_clip_start(item, t);
			else item.start = t;
		},
		set_clip_start (clip, value) {
			const v = parseFloat(value);
			if (!isNaN(v)) clip.start = fit_start(this.timeline, clip, clip.track, Math.max(0, v));
		},
		set_in (clip, value) {
			const v = parseFloat(value);
			if (isNaN(v)) return;
			clip.in = _.clamp(v, Math.max(0, clip.out - max_len(this.timeline, clip)), clip.out - MIN_LEN);
		},
		set_out (clip, value) {
			const v = parseFloat(value);
			if (isNaN(v)) return;
			clip.out = _.clamp(v, clip.in + MIN_LEN, Math.min(clip.source_duration, clip.in + max_len(this.timeline, clip)));
		},
		set_duration (clip, value) {
			const v = parseFloat(value);
			if (isNaN(v)) return;
			clip.out = clip.in + _.clamp(v, MIN_LEN, Math.min(clip.source_duration - clip.in, max_len(this.timeline, clip)));
		},
		set_quality (clip, uuid) {
			const q = _.find(clip.qualities, {uuid: uuid});
			if (!q || _.get(clip, "source.clip_uuid") == uuid) return;
			clip.url = q.url;
			clip.source = Object.assign({}, clip.source, {clip_uuid: q.uuid, quality: q.name});
		},
		remove_clip (clip) {
			this.timeline = _.reject(this.timeline, {id: clip.id});
			if (this.selected_id == clip.id) this.selected_id = undefined;
			this.pair_id = undefined;
		},
		duplicate_clip (clip) {
			const copy = _.cloneDeep(clip);
			copy.id = this.next_id++;
			copy.start = fit_start(this.timeline, copy, clip.track, clip.start + (clip.out - clip.in));
			this.timeline.splice(_.findIndex(this.timeline, {id: clip.id}) + 1, 0, copy);
			this.selected_id = copy.id;
			this.pair_id = undefined;
		},
		remove_overlay (overlay) {
			this.overlays = _.reject(this.overlays, {id: overlay.id});
			if (this.selected_overlay_id == overlay.id) this.selected_overlay_id = undefined;
		},
		duplicate_overlay (overlay) {
			const copy = _.cloneDeep(overlay);
			copy.id = this.next_id++;
			copy.start = overlay.start + overlay.duration;
			this.overlays.push(copy);
			this.select_overlay(copy);
		},
		remove_audio (audio) {
			this.audios = _.reject(this.audios, {id: audio.id});
			if (this.selected_audio_id == audio.id) this.selected_audio_id = undefined;
		},
		duplicate_audio (audio) {
			const copy = _.cloneDeep(audio);
			copy.id = this.next_id++;
			copy.start = audio.start + audio.duration;
			this.audios.push(copy);
			this.select_audio(copy);
		},
		clear_timeline () {
			this.pause();
			this.timeline = [];
			this.overlays = [];
			this.audios = [];
			this.reset_tracks();
			this.clear_selection();
			this.playhead = 0;
		},
		split () {
			const t = this.current_time();
			const at_playhead = (item) => t >= item.start && t < item.end;
			// Prefer the selected clip, otherwise the frontmost clip under the playhead
			const l = _.find(this.layout, (item) => item.clip.id == this.selected_id && at_playhead(item))
				|| _.minBy(_.filter(this.layout, at_playhead), (item) => _.findIndex(this.tracks, {id: item.track}));
			if (!l) return;
			const clip = l.clip;
			const elapsed = t - l.start;
			if (elapsed < MIN_LEN || l.len - elapsed < MIN_LEN) {
				this.$message.warning("Playhead is too close to clip edge");
				return;
			}
			const second = _.cloneDeep(clip);
			second.id = this.next_id++;
			second.start = clip.start + elapsed;
			second.transition = {type: "cut", duration: clip.transition.duration};
			clip.transition_out = {type: "cut", duration: out_transition(second).duration};
			if (clip.type == "image") {
				second.in = 0;
				second.out = l.len - elapsed;
				clip.out = clip.in + elapsed;
			} else {
				second.in = clip.in + elapsed;
				clip.out = second.in;
			}
			this.timeline.splice(l.gindex + 1, 0, second);
		},
		select_transition (l) {
			const prev = _.find(this.timeline, {id: l.xfade.prev_id});
			if (!prev) return;
			this.select(prev);
			this.pair_id = l.clip.id;
		},
		set_pair_transition (type) {
			const pair = this.pair;
			if (!pair || !pair.consecutive) return;
			pair.second.transition.type = type;
			pair.second.start = pair.first.start + (pair.first.out - pair.first.in);
		},

		// ---- Layout
		split_start () {
			this.split_origin = {
				sources: this.sources_width,
				inspector: this.inspector_width,
				timeline: this.$refs.timeline.$el.offsetHeight
			};
		},
		move_split (kind, {dx, dy}) {
			const d = this.split_origin;
			if (!d) return;
			const parent = this.$refs.timeline.$el.parentElement;
			if (kind == "bottom") {
				const max = parent.clientHeight - 260 - 12;
				this.timeline_height = _.clamp(d.timeline - dy, 120, Math.max(120, max));
				return;
			}
			const total = parent.clientWidth - 24 - 320;
			if (kind == "left") {
				this.sources_width = _.clamp(d.sources + dx, 180, Math.max(180, total - this.inspector_width));
			} else {
				this.inspector_width = _.clamp(d.inspector - dx, 180, Math.max(180, total - this.sources_width));
			}
		},

		// ---- Playback
		toggle_play () {
			this.playing ? this.pause() : this.play();
		},
		play () {
			if (this.end_time <= 0) return;
			this.pos = this.playhead >= this.end_time ? 0 : this.playhead;
			this.playing = true;
			this.last_ts = performance.now();
			this.raf = requestAnimationFrame(this.tick);
		},
		pause () {
			if (this.playing) {
				this.playing = false;
				cancelAnimationFrame(this.raf);
				this.playhead = this.pos;
			}
			this.sync_players();
		},
		seek (t) {
			this.playhead = _.clamp(t, 0, this.end_time);
			this.pos = this.playhead;
		},
		scrub (t) {
			this.playhead = _.clamp(t, 0, this.end_time);
		},
		// While playing, the position is kept outside Vue's reactivity so the template is not re-rendered every frame
		current_time () {
			return this.playing ? this.pos : this.playhead;
		},
		tick (ts) {
			if (!this.playing) return;
			this.pos += (ts - this.last_ts) / 1000;
			this.last_ts = ts;
			if (this.pos >= this.end_time) {
				this.pos = this.end_time;
				this.pause();
				return;
			}
			this.update_time_display();
			this.sync_players();
			this.raf = requestAnimationFrame(this.tick);
		},
		update_time_display () {
			if (this.$refs.controls) this.$refs.controls.show_time(this.pos);
			if (this.$refs.timeline) this.$refs.timeline.show_playhead(this.pos);
		},
		sync_players () {
			if (this.$refs.preview) this.$refs.preview.sync(this.current_time(), this.playing, this.trimming);
		},
		on_trim (trimming) {
			this.trimming = trimming;
			if (trimming) this.sync_players();
		},

		// ---- Keyboard
		key_down (event) {
			const target = event.target || {};
			const tag = (target.tagName || "").toLowerCase();
			if (tag == "input" || tag == "textarea" || tag == "select" || target.isContentEditable) return;
			if (event.ctrlKey || event.metaKey || event.altKey) return;
			if (event.code == "Space") {
				// Otherwise Space would also click the focused button
				if (tag == "button") target.blur();
				event.preventDefault();
				this.toggle_play();
			} else if (event.key == "Delete" || event.key == "Backspace") {
				event.preventDefault();
				this.delete_selected();
			} else if (event.key == "s" || event.key == "S") {
				this.split();
			}
		},

		// ---- Output
		build_edl () {
			return build_edl({
				title: this.project_title,
				duration: this.end_time,
				tracks: this.tracks,
				layout: this.layout,
				overlays: this.overlays,
				audios: this.audios
			});
		},
		save () {
			this.$emit("save", this.build_edl());
		},
		download () {
			const blob = new Blob([JSON.stringify(this.build_edl(), null, 2)], {type: "application/json"});
			const a = document.createElement("a");
			a.href = URL.createObjectURL(blob);
			a.download = (this.project_title || "timeline") + ".json";
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			setTimeout(() => URL.revokeObjectURL(a.href), 1000);
		},
		import_file (event) {
			const file = event.target.files[0];
			event.target.value = "";
			if (!file) return;
			const reader = new FileReader();
			reader.onload = () => {
				try {
					this.load_edl(JSON.parse(reader.result));
					this.$message.success("Timeline imported");
				} catch (e) {
					this.$message.warning("Unable to import: " + e.message);
				}
			};
			reader.onerror = () => { this.$message.warning("Unable to read file"); };
			reader.readAsText(file);
		},
		load_edl (edl) {
			const loaded = parse_edl(edl, () => this.next_id++);
			this.pause();
			this.project_title = loaded.title;
			this.tracks = loaded.tracks;
			this.timeline = loaded.clips;
			this.overlays = loaded.overlays;
			this.audios = loaded.audios;
			this.active_track = {
				video: _.find(loaded.tracks, {kind: "video"}).id,
				overlay: _.find(loaded.tracks, {kind: "overlay"}).id,
				audio: _.find(loaded.tracks, {kind: "audio"}).id
			};
			this.track_counter = count_tracks(loaded.tracks);
			this.clear_selection();
			this.playhead = 0;
		}
	}
}
</script>

<style src="./video_editor/common.css"></style>

<style scoped>
.ks-videoeditor-title-field { display: inline-block; width: 220px; margin-right: 8px; vertical-align: middle; }

.ks-videoeditor-body { flex: 1 1 auto; min-height: 520px; display: flex; flex-direction: column; }
.ks-videoeditor-top { flex: 1 1 0; min-height: 260px; display: grid; grid-template-columns: 280px 12px minmax(0, 1fr) 12px 280px; grid-template-rows: minmax(0, 1fr); }
.ks-videoeditor-preview { display: flex; flex-direction: column; min-width: 0; min-height: 0; }
</style>
